import { useContext } from 'react';

import { K8sProvider } from '@cast/shared-types';
import { AgentStatus, Cluster } from '@cast/types';
import { isManagedByTerraform } from '@cast/utils';

import { ClusterContext } from './ClusterProvider';
import { MANAGED_PROVIDERS } from './constants';
import { isClusterManaged } from './utils/isClusterManaged';
import {
  isClusterOnboardingPhase1,
  isClusterOnboardingPhase2,
} from './utils/isClusterOperable';
import { isKentEligibleCluster, isKentManagedCluster } from './utils/kent';

type UseClusterHook = {
  cluster: Cluster;
  error: string | undefined;
  isManaged: boolean;
  isLoading: boolean;
  isOnline: boolean;
  isDisconnected: boolean;
  isCastAnywhere: boolean;
  canBeManaged: boolean;
  isTerraformManaged: boolean;
  clusterOnboardingPhase1: boolean;
  clusterOnboardingPhase2: boolean;
  isKentManaged: boolean;
  isKentEligible: boolean;
};

export const useCluster = (): UseClusterHook => {
  const { cluster, isLoading, error } = useContext(ClusterContext);
  const isKentManaged = isKentManagedCluster(cluster);
  const isKentEligible = isKentEligibleCluster(cluster);
  const isManaged = isClusterManaged(cluster);
  const isOnline = cluster.agentStatus === AgentStatus.ONLINE;
  const isDisconnected = cluster.agentStatus === AgentStatus.DISCONNECTED;
  const isCastAnywhere = cluster.providerType === K8sProvider.ANYWHERE;
  const canBeManaged = MANAGED_PROVIDERS.includes(cluster.providerType);
  const isTerraformManaged = isManagedByTerraform(cluster.managedBy);
  const clusterOnboardingPhase1 = isClusterOnboardingPhase1(
    cluster.displayStatus,
    isManaged
  );
  const clusterOnboardingPhase2 = isClusterOnboardingPhase2(
    cluster.status,
    isManaged
  );

  return {
    cluster,
    error,
    isLoading,
    isManaged,
    isOnline,
    isDisconnected,
    isCastAnywhere,
    canBeManaged,
    isTerraformManaged,
    clusterOnboardingPhase1,
    clusterOnboardingPhase2,
    isKentManaged,
    isKentEligible,
  };
};
